import * as React from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import { Button } from "@/components/ui/button"
import { Item } from "@/types/purchase"
import { Zap, Search, Trash2, X, Package } from "lucide-react" // Added Search, Trash2, X icons
import { Input } from "@/components/ui/input" // Added Input component
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

import { Skeleton } from "@/components/ui/skeleton"
import { StockBadge } from "@/components/inventory/StockBadge"
import { normalizeItemName } from "@/supabase/inventory/normalizeItemName"
import { useMediaQuery } from "@/hooks/useMediaQuery"
import type { QuickAddStockByNorm } from "@/hooks/useInventoryQuickAdd"

interface RecentItemsSectionProps {
  items: Item[];
  onItemClick: (item: Item) => void;
  isLoading: boolean;
  onDeleteItem: (itemName: string) => void;
  onClearAllItems: () => void;
  isHighlighting?: boolean; // Added
  // PR6 재고 quick-add(옵셔널) — 미주입/null이면 아래 어느 분기에도 진입하지 않아 기존과 픽셀
  // 동일. 주입 시 상단에 '내 상품' 고정 그룹(재고 뱃지, 삭제 버튼 없음 — 관리는 /inventory에서)
  // + 기존 최근 상품 목록에서 마스터와 norm 중복 칩 숨김(이중 표시 방지). 클릭은 동일한
  // onItemClick(item) 경로 재사용(플랜 §배선 3).
  inventoryMasterItems?: Item[] | null;
  inventoryStockByNorm?: QuickAddStockByNorm | null;
}

export function RecentItemsSection({ items, onItemClick, isLoading, onDeleteItem, onClearAllItems, isHighlighting, inventoryMasterItems, inventoryStockByNorm }: RecentItemsSectionProps) {
  const [searchTerm, setSearchTerm] = React.useState("");

  // 둘 다 같은 훅 state에서 내려와 항상 함께 null/비-null이지만, 방어적으로 둘 다 있을 때만
  // 그룹을 켠다(fail-closed — 뱃지 재료 없이 마스터만 노출되는 어중간한 상태 차단).
  const masterItems = inventoryMasterItems ?? null;
  const stockByNorm = inventoryStockByNorm ?? null;
  const hasMasterGroup = !!masterItems && masterItems.length > 0 && !!stockByNorm;

  const masterNorms = React.useMemo(() => {
    if (!hasMasterGroup) return null;
    return new Set(masterItems.map(item => normalizeItemName(item.name)));
  }, [hasMasterGroup, masterItems]);

  // 마스터와 norm이 겹치는 최근 상품 칩은 숨긴다 — 같은 상품이 두 번 보이면 어느 쪽을 눌러야
  // 하는지 혼란(둘 다 같은 onItemClick이지만 뱃지는 마스터 쪽에만 있음).
  const catalogItems = React.useMemo(() => {
    if (!masterNorms) return items;
    return items.filter(item => !masterNorms.has(normalizeItemName(item.name)));
  }, [items, masterNorms]);

  const filteredItems = React.useMemo(() => {
    if (!searchTerm) {
      return catalogItems;
    }
    return catalogItems.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [catalogItems, searchTerm]);

  // 카탈로그 그리드는 행 단위 가상 스크롤(플랜 docs/plan-recent-items-virtual-scroll.md). 칩 전량
  // 렌더(#164 사고)도, 렌더 상한 100("100개까지만 보인다" CS)도 아닌 제3의 길 — 동시 마운트 칩 수는
  // 카탈로그 크기와 무관한 상한(md 4열 최대 약 40칩)을 가진다. 상한 수치를 다시 넣지 말 것.
  //
  // 열 수의 단일 소스: 기존 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 (Tailwind 기본 screens
  // sm 640 / md 768)를 같은 값으로 미러. 행 슬라이스와 행 그리드 열 수를 이 cols 하나로 그린다.
  const isMd = useMediaQuery("(min-width: 768px)");
  const isSm = useMediaQuery("(min-width: 640px)");
  const cols = isMd ? 4 : isSm ? 3 : 2;
  const rowCount = Math.ceil(filteredItems.length / cols);
  const parentRef = React.useRef<HTMLDivElement>(null);
  // 행 key = 행의 첫 칩 이름(유저별 upsert 유일). 첫 칩이 바뀐 행은 새 key로 remount·재측정,
  // 첫 칩이 유지되는 행은 ref/ResizeObserver 측정으로 교정. useCallback 필수 — virtual-core는
  // getItemKey 참조를 measurement 메모 의존성으로 쓰므로 인라인이면 스크롤 렌더마다 전 행 재계산.
  const getRowKey = React.useCallback(
    (rowIndex: number) => filteredItems[rowIndex * cols]?.name ?? rowIndex,
    [filteredItems, cols],
  );
  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 60, // 1줄 이름 칩(~52px) + 행 간격 8px. 마운트 시 offsetHeight로 즉시 교정
    overscan: 2,
    getItemKey: getRowKey,
  });
  // 검색어가 바뀌거나 로딩(스켈레톤)에서 돌아오면 목록 맨 위로. virtualizer 인스턴스는 유지되므로
  // 새 스크롤 요소에 옛 scrollOffset을 복원하고, 같은 커밋의 보정 쓰기(행 첫 측정·anchor)가 onChange
  // 시점 scrollTo(0)을 덮는다(브라우저 실측: 결과 맨 아래로 튐) → 훅의 layout effect 뒤에 도는 여기서.
  // 스크롤 요소가 없을 때(로딩·빈 목록·jsdom)는 라이브러리가 `?.`로 가드.
  React.useLayoutEffect(() => {
    if (!isLoading) rowVirtualizer.scrollToOffset(0);
  }, [rowVirtualizer, searchTerm, isLoading]);

  // 검색은 마스터 그룹에도 동일 기준으로 적용 — 검색 중 마스터만 전부 남아 있으면 오히려
  // 찾는 상품이 묻힌다. raw 매칭에 더해 norm 폴백(공백 무시)도 허용한다(codexloop R1 P2 수용):
  // 마스터 "ABC"가 카탈로그 "A B C"를 숨긴 상태에서 "A B"로 검색하면 raw만으로는 양쪽 다
  // 안 잡혀 "검색 결과가 없습니다"가 뜨는데, 그 상품은 실제로 존재한다 — norm("A B")="ab"가
  // norm("ABC")="abc"에 부분일치하므로 마스터 칩이 대표로 잡힌다. 공백-only 검색어는
  // norm이 ''라 전체 일치가 돼버리므로 폴백에서 제외.
  const filteredMasterItems = React.useMemo(() => {
    if (!hasMasterGroup) return null;
    if (!searchTerm) return masterItems;
    const query = searchTerm.toLowerCase();
    const normQuery = normalizeItemName(searchTerm);
    return masterItems.filter(item =>
      item.name.toLowerCase().includes(query) ||
      (normQuery !== "" && normalizeItemName(item.name).includes(normQuery))
    );
  }, [hasMasterGroup, masterItems, searchTerm]);

  const showMasterSection = !!filteredMasterItems && filteredMasterItems.length > 0 && !!stockByNorm;

  if (isLoading) {
    return (
      <div className="space-y-3 bg-gray-50 p-4 rounded-lg shadow-sm">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-medium text-gray-700 flex items-center">
            <Zap className="w-4 h-4 mr-2 text-yellow-500" />
            최근 추가한 상품 빠르게 추가
          </h3>
        </div>
        <Skeleton className="h-9 w-full" />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 items-start">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-[60px] w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className={`space-y-3 p-4 rounded-lg shadow-sm transition-all duration-500 ${isHighlighting ? 'bg-indigo-50 ring-4 ring-indigo-300 ring-offset-2 animate-pulse' : 'bg-gray-50'}`}>
        <div className="flex justify-between items-center">
          <h3 className={`text-sm font-medium flex items-center ${isHighlighting ? 'text-indigo-900 font-bold' : 'text-gray-700'}`}>
            <Zap className={`w-4 h-4 mr-2 ${isHighlighting ? 'text-indigo-600 animate-bounce' : 'text-yellow-500'}`} />
            최근 추가한 상품 빠르게 추가
          </h3>
          {/* 게이트를 raw items가 아닌 catalogItems(중복 숨김 반영)로: 보이는 최근 칩이 0인데
              버튼만 남아 "눌러도 화면 무변화"가 되는 혼란 차단(codexloop R1 P2 수용).
              마스터 미주입 시 catalogItems === items(동일 참조)라 기존과 픽셀 동일. */}
          {catalogItems.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (window.confirm("정말로 모든 상품을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.")) {
                  onClearAllItems();
                }
              }}
              className="text-xs text-gray-500 hover:text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              전체 삭제
            </Button>
          )}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="상품명 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 w-full text-xs h-9"
          />
        </div>

        {showMasterSection && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Package className="w-3.5 h-3.5 text-indigo-500" />
              <span className="text-xs font-semibold text-gray-700">내 상품</span>
              <span className="text-[11px] text-gray-400">재고 연동</span>
            </div>
            <div className="max-h-40 overflow-y-auto pr-2">
              {/* 열 수를 뷰포트가 아니라 컨테이너 폭으로 결정(auto-fill minmax 140px) — 카탈로그
                  그리드의 md:cols-4는 뷰포트 기준이라 xl 3분할에서 칩이 ~94px까지 좁아져 뱃지가
                  잘렸다(1440px 실측). 140px 하한이면 최장 가격(14,000원)+뱃지(-13)가 항상 한 줄에
                  들어간다 — 칩 모양이 어느 폭에서든 동일(오너 요구: 뱃지 완전 노출 + 일관성). */}
              <ul className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-2">
                {filteredMasterItems.map((item) => {
                  const stockInfo = stockByNorm?.get(normalizeItemName(item.name));
                  return (
                    <li key={`inventory-${item.name}`} className="min-w-0">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start h-auto py-2 px-3 text-xs border-indigo-200 bg-indigo-50/40 rounded-md hover:bg-indigo-50 hover:border-indigo-400"
                            onClick={() => onItemClick(item)}
                          >
                            {/* 모든 칩이 동일한 2행 구조(높이 균일 = 일관성): 1행 = 이름 한 줄
                                말줄임(전체 이름·가용은 툴팁), 2행 = 가격(양보·말줄임) + 뱃지
                                (shrink-0 — 절대 안 눌리고 항상 우하단 같은 자리). */}
                            <div className="flex w-full flex-col items-start gap-0.5 overflow-hidden">
                              <span className="w-full truncate text-left font-medium text-gray-800">
                                {item.name}
                              </span>
                              <span className="flex w-full items-center justify-between gap-1.5">
                                <span className="min-w-0 truncate text-gray-500">{item.price.toLocaleString()}원</span>
                                {stockInfo && (
                                  <span className="shrink-0">
                                    <StockBadge available={stockInfo.available} threshold={stockInfo.threshold} />
                                  </span>
                                )}
                              </span>
                            </div>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            {item.name}
                            {stockInfo ? ` · 가용 ${stockInfo.available.toLocaleString()}개` : ""}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}

        {filteredItems.length > 0 ? (
          <>
          <div ref={parentRef} className="max-h-60 overflow-y-auto pr-2">
            <div style={{ height: rowVirtualizer.getTotalSize(), position: "relative" }}>
              {rowVirtualizer.getVirtualItems().map((vRow) => {
                const start = vRow.index * cols;
                const rowItems = filteredItems.slice(start, start + cols);
                return (
                  // 행 간격 8px은 pb-2(마지막 행 제외)로 재현 — 측정이 offsetHeight라 margin은 안
                  // 잡힌다. repeat(N, minmax(0,1fr))은 Tailwind grid-cols-N 정의와 동일.
                  // 행 오프셋은 transform 금지 → top. 칩 툴팁은 Portal 없이 칩 안에 Radix popper
                  // 래퍼(position: fixed)로 렌더되므로, 행에 transform이 있으면 그 행이 containing
                  // block이 되어 툴팁이 스크롤 박스에 잘린다(2026-09-05 CS: 첫 행 툴팁 안 보임).
                  <ul
                    key={vRow.key}
                    data-index={vRow.index}
                    ref={rowVirtualizer.measureElement}
                    className={`absolute left-0 w-full grid gap-2 items-start${vRow.index < rowCount - 1 ? " pb-2" : ""}`}
                    style={{
                      top: vRow.start,
                      gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                    }}
                  >
              {rowItems.map((item) => {
                return (
                  <li key={item.name} className="min-w-0">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="relative group/item">
                          <Button
                            variant="outline"
                            className="w-full justify-start h-auto py-2 px-3 text-xs border-gray-300 rounded-md hover:bg-gray-100 hover:border-indigo-400"
                            onClick={() => onItemClick(item)}
                          >
                            <div className="flex flex-col items-start w-full overflow-hidden">
                              <span
                                className="font-medium text-gray-800 group-hover:text-indigo-600 break-all min-w-0"
                              >
                                {item.name}
                              </span>
                              <span className="text-gray-500 group-hover:text-indigo-500">{item.price.toLocaleString()}원</span>
                            </div>
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="absolute top-1 right-1 h-6 w-6 text-gray-400 rounded-full opacity-0 group-hover/item:opacity-100 hover:bg-red-100 hover:text-red-600 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent Button parent from being clicked
                              onDeleteItem(item.name);
                            }}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{item.name}</p>
                      </TooltipContent>
                    </Tooltip>
                  </li>
                );
              })}
                  </ul>
                );
              })}
            </div>
          </div>
          {/* 현행 문구("나머지 N개는 검색으로")와 같은 조건(>100)에서만 표시 — ≤100 셀러는 픽셀
              동일. 이제 숨겨진 상품이 없으므로 "100개만 저장" 오해를 막는 전체 개수 문구로 교체. */}
          {filteredItems.length > 100 && (
            <p className="text-[11px] text-gray-400 text-center">
              {searchTerm
                ? `검색 결과 ${filteredItems.length.toLocaleString()}개`
                : `전체 ${filteredItems.length.toLocaleString()}개 · 최근 사용순`}
            </p>
          )}
          </>
        ) : showMasterSection ? null : (
          // '내 상품' 그룹이 보이는 동안은 최근 목록의 빈 상태 문구를 접는다 — 마스터 칩이
          // 떠 있는데 바로 아래 "등록된 상품이 없습니다"가 뜨면 모순으로 읽힌다(중복 숨김으로
          // 최근 목록만 빌 수 있음). 그룹이 없거나 검색이 양쪽 다 비우면 기존 문구 그대로.
          <div className="text-xs text-gray-500 text-center py-4 px-3 border-2 border-dashed border-gray-200 rounded-md bg-gray-50/30">
            <p className="font-medium">
              {searchTerm ? "검색 결과가 없습니다." : "등록된 상품이 없습니다."}
            </p>
            <p className="mt-0.5">
              {searchTerm ? "다른 검색어로 시도해보세요." : "상품을 추가하면 여기에 표시됩니다."}
            </p>
          </div>
        )}
      </div>
    </TooltipProvider>
  )
}